import {type GqlFetchFn} from "@khan/data-gql";
import {LocaleStatus} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {
    getClassroomUrl,
    getTransferAuthUrl,
    HostnameParts,
    parseUrl,
    validateContinueUrl,
    type Location,
} from "@khan/urls";

import type {loggedInUserQueryType} from "../graphql/__graphql-types__/index.ts";
import {CALCULATE_NEXT_URL_USER_QUERY} from "../graphql/calculate-next-url-user-query.ts";
import {TRANSFER_AUTH_TOKEN_MUTATION} from "../graphql/transfer-auth-token-mutation.ts";
import type {
    JoinClassroomsData,
    JoinClassroomsError,
    LoginType,
} from "../shared/types.ts";

/**
 * Given a specific error code, return a URL where the user needs to be
 * redirected to.
 *
 * @param {JoinClassroomsError} error The join classrooms error code.
 * @returns {string} The URL where the user should be redirected to.
 */
const getUrlFromError = (error: JoinClassroomsError) => {
    switch (error.code) {
        case "NOT_ALLOWED":
            return error.classroomName
                ? `/coaches?restrictedClassName=${error.classroomName}`
                : "";
        case "CLASSROOM_NOT_FOUND":
            return `/coaches?failedCode=${error.signupCode}`;
        case "DISTRICT_SYNCED_CLASSROOM":
            return `/coaches?failedCode=${error.signupCode}`;
        case "CANNOT_ADD_COACH":
            return `/coaches?failedCode=${error.signupCode}`;
        default:
            return "";
    }
};

/**
 * Build a redirect URL based on a list of classes the user has joined or use it
 * to activate a district student.
 *
 * We first check whether any of the class codes provided can be used to link
 * the user to a district's UserDistrictInfo. If one of the class codes
 * corresponds to a district-synced Classroom, we will either either
 * successfully link the user or redirect them to a location to ask them for
 * some follow-up information to complete the linking.
 *
 * If these class codes do not correspond to district-synced Classrooms, then
 * proceed to join each classroom as they are all grassroots classrooms, and
 * return a redirect URL for these flows.
 *
 * @see JoinGrassrootClassroomMutation
 *
 * @param {JoinClassroomsData} joinClassrooms
 * @returns {string} The continue Url passed from the list of classes the user
 * has joined.
 */
export const buildRedirectUrlFromJoinedClasses = (
    joinClassrooms: JoinClassroomsData,
): string => {
    // There is an error trying to join one or more classes.
    if (joinClassrooms.errors) {
        for (const error of joinClassrooms.errors) {
            const url = getUrlFromError(error);

            // If we identify one of the errors, then we return the
            // corresponding URL here.
            if (url !== "") {
                return url;
            }
        }
    }

    // No errors found and the user has joined a valid class.
    if (joinClassrooms.studentLists) {
        for (const studentList of joinClassrooms.studentLists) {
            if (studentList?.id) {
                return "/profile/me/class/" + studentList.signupCode;
            }
        }
    }

    // This is a very specific case where we didn't get any errors and the user
    // didn't join a class.
    return "/coaches";
};

/**
 * Return the next url where the users have to navigate after being logged in
 * for the first time.
 *
 * @param {string} baseUrl The URL where the users should navigate to.
 * @param {string} continueUrl An optional URL where the users might need to
 * redirect.
 * @returns {string} The next URL.
 */
function getNextUrl(baseUrl: string, continueUrl?: string) {
    if (!continueUrl) {
        return baseUrl;
    }

    return `${baseUrl}?continue=${encodeURIComponent(continueUrl)}`;
}

/**
 * Get the next url for a user that is logging in for the first time.
 *
 * In some cases there are extra signup steps that are inserted into the the
 * workflow:
 *
 * Some phantom users will see a "migrating data" page Parents will be
 * redirected to create a child account.
 *
 * Finally, users are returned to whatever page is specified in returnTo.
 * Sometimes this is overridden as part of an experiment. Common overrides for
 * this location are the homepage and the profile page.
 *
 * @param {NonNullable<loggedInUserQueryType["response"]["user"]>} userData The data for the current User.
 * @param {string} returnTo  Where the user is supposed to return when the
 * signup process is complete.
 * @returns {string} The next URL for a user that is logging in for the first
 * time.
 */
export const nextUrlAfterFirstTimeLogin = (
    userData: NonNullable<loggedInUserQueryType["response"]["user"]>,
    returnTo: string,
): string => {
    // If the user needs to create a child account, redirect them.

    // If a parent clicks on the "create account" link in the email they
    // received, they will already have a /parent in their continue:
    const isGoingToCreateChild: boolean = returnTo.includes("parent");

    // But if they came here of their own accord, we tell them to create
    // child accounts if we know they have invitations to resolve.
    if (!isGoingToCreateChild && userData.hasUnresolvedInvitations) {
        // We should replace this with a route that directly takes the user
        // to the Add Child Workflow. https://khanacademy.atlassian.net/browse/CLASS-6148
        return getNextUrl("/parent", returnTo);
    }

    return getNextUrl(returnTo);
};

/**
 * Verify if the user needs to be transfered to a different domain, and if
 * that's the case override the nextUrl value with the one defined in
 * transferAuthUrl.
 *
 * @param {NonNullable<loggedInUserQueryType["response"]["user"]>} userData The data for the current User.
 * @param {string} nextUrl  Where the user is supposed to return when the
 * signup process is complete.
 * @returns {string} Either the transfer auth URL or the nextUrl initially
 * passed in.
 */
export const maybeAddAuthTransfer = async (
    gqlFetch: GqlFetchFn,
    location: Location,
    userData: NonNullable<loggedInUserQueryType["response"]["user"]>,
    nextUrl: string,
): Promise<string> => {
    const defaultDistrictLocale =
        userData.userDistrictInfos?.[0]?.district?.kaLocale;

    let preferredLocale: string | null | undefined = undefined;
    let status: LocaleStatus | null | undefined = undefined;
    if (userData.preferredKaLocale) {
        preferredLocale = userData.preferredKaLocale.kaLocale;
        status = userData.preferredKaLocale.status as LocaleStatus;
    } else if (defaultDistrictLocale) {
        // We are assuming that if a locale is associated with a district, it is
        // live.
        preferredLocale = defaultDistrictLocale;
        status = LocaleStatus.LIVE;
    }

    const currentLocale = RequestInfo.kaLocale;
    const hostnameParts = HostnameParts.parse(location.hostname);

    // The user is using the same locale, so reuse the nextUrl passed as arg.
    if (
        hostnameParts.lang !== "www" ||
        !preferredLocale ||
        preferredLocale === currentLocale
    ) {
        return nextUrl;
    }

    // The user has landed from a different locale, so use the transferAuthToken.
    const result = await gqlFetch(TRANSFER_AUTH_TOKEN_MUTATION, {});

    const transferAuthToken = result.createTransferAuthToken?.token;
    if (!transferAuthToken) {
        return nextUrl;
    }

    // Verify if the transferAuthUrl is a valid KA url.
    const transferAuthUrlString: string = getTransferAuthUrl(
        transferAuthToken,
        location.pathname,
        location.queryParams,
        preferredLocale,
        // TODO(marksandstrom) See if there's a typescript bug for this. See:
        // https://github.com/Khan/frontend/pull/1886#discussion_r2208920722
        status as LocaleStatus | null,
    );
    const validatedTransferAuthUrl = transferAuthUrlString || nextUrl;

    // try to add auth transfer only if it's a valid KA url. Otherwise,
    // return the initial URL.
    if (validatedTransferAuthUrl === nextUrl) {
        return nextUrl;
    }

    // Add origin as backup in case the transfer auth is relative.
    const transferAuthUrl: URL = parseUrl(validatedTransferAuthUrl);
    const includesContinue = transferAuthUrl.searchParams.has("continue");

    // If the transfer URL contains the `continue` query param, then we
    // replace the value by the one we passed as an argument.
    if (includesContinue) {
        transferAuthUrl.searchParams.set("continue", nextUrl);
    }

    // Override the continueUrl with the parsed transfer auth url.
    return transferAuthUrl.href;
};

/**
 * Verifiy if the current request is coming from the clever library or not.
 *
 * @param {LoginType} loginType The login type used (we expect clever or
 * clever_login).
 * @param {boolean} cleverLibrary Whether we are coming from the clever library.
 * @param {boolean} linkClever TBD
 *
 * @returns {boolean} Whether the user is coming from the clever library.
 */
const isCleverLibraryUser = (loginType: LoginType, linkClever?: boolean) => {
    if (linkClever || loginType === "clever_library") {
        return true;
    }

    return false;
};

export interface RedirectOptions {
    gqlFetch: GqlFetchFn;
    location: Location;
    loginType: LoginType;
    continueUrl: string;
    isFirstLogin: boolean;
    joinClassrooms?: JoinClassroomsData;
    linkClever?: boolean;
}

/**
 * Calculate the continue URL after the user has been authenticated in the
 * system. This will help us determine with more precision where the user should
 * be redirected to.
 *
 * @param {RedirectOptions} options The list of available options to calculate
 * the continue URL.
 * @param {LoginUserData} options.userData The user-relevant data to obtain the
 * continue URL.
 * @param {LoginType} options.loginType The kind of third-party account the user
 *   has authenticated with.
 * @param {string} options.continueUrl The kind of third-party account the user
 *   has authenticated with.
 * @param {boolean} options.isFirstLogin Whether the user is being logged in for
 * the first time.
 * @param {JoinClassroomsData} options.joinClassrooms An optional object to know
 * if the user has joined a classroom or has gotten any errors in the process of
 * joining one or more classes.
 * @param {boolean} options.linkClever Whether the user is using clever to
 * authenticate.
 *
 * @returns {string} The continue URL based on the input data.
 */
export const calculateNextUrl = async ({
    gqlFetch,
    location,
    loginType,
    continueUrl,
    isFirstLogin,
    joinClassrooms,
    linkClever,
}: RedirectOptions): Promise<string> => {
    let nextUrl = "";
    // overrideParentOnboarding is used to prevent returning users who
    // sign back in with apple, google, or facebook from getting put
    // through the onboarding flow. We only know after they log in whether
    // they are already a user.
    // NOTE(kairui): This is legacy code. continue URL being /parent
    // does not indicate that the log in is from a parent. It is simply the
    // continue URL sent to the u13 parent invite emails.
    const overrideParentOnboarding =
        ["apple", "google", "facebook"].includes(loginType) &&
        // We should replace this with a route that directly takes the user
        // to the Add Child Workflow. https://khanacademy.atlassian.net/browse/CLASS-6148
        continueUrl === "/parent";

    const decodedContinueUrl = decodeString(continueUrl);
    // For users who are signing up from our Khanmigo join flow, it is
    // important to take them back to that flow so they can complete the
    // checkout process.
    const isKhanLabsContinueUrl = decodedContinueUrl.includes("/khan-labs");
    const isKhanmigoCheckoutContinueUrl =
        decodedContinueUrl.includes("/khanmigo/checkout");
    const isKhanmigoCheckoutFlow =
        isKhanLabsContinueUrl || isKhanmigoCheckoutContinueUrl;
    const isAKhanmigoEnrollmentFlow =
        isKhanmigoCheckoutFlow ||
        decodedContinueUrl.includes("/khanmigo/teacher-tools");
    const isWritingCoach = location.pathname.includes("/writing-coach");
    // If class codes are provided, the server tries to register the user for
    // these classes and the client is in charge of building the redirect URL
    // based on the response.
    if (joinClassrooms) {
        continueUrl = buildRedirectUrlFromJoinedClasses(joinClassrooms);
    }

    // If the path is /writing-coach, we always want to land on writing-coach
    // unless there's an explicit continueUrl
    if (isWritingCoach) {
        continueUrl =
            decodedContinueUrl !== "/" ? decodedContinueUrl : "/writing-coach/";
    }

    // Verify if the current user is coming from Clever.
    const cleverLibraryUser = isCleverLibraryUser(loginType, linkClever);

    if (continueUrl === "") {
        continueUrl = "/";
    }

    // Logging in with Clever Library appends ?fromCleverLibrary=1 to
    // the continue URL.
    if (cleverLibraryUser) {
        const divider = continueUrl.includes("?") ? "&" : "?";
        continueUrl += divider + "fromCleverLibrary=1";
    }

    // Now that we know who the user is, re-compute the real continue url,
    // if it wasn't specified (as the default may differ depending on who's
    // logged in)
    let validatedContinueUrl = validateContinueUrl(continueUrl, "/");

    const hostnameParts = HostnameParts.parse(location.hostname);

    if (hostnameParts.lang === "admin") {
        return validatedContinueUrl;
    }

    if (overrideParentOnboarding) {
        validatedContinueUrl = "/";
    }

    // Is a new reg flow "extra info" page
    const isExtraInfoPage = Boolean(validatedContinueUrl.match(/extra-info/i));

    // Fetch user data needed for the rest of the redirect logic
    const userQueryResult = await gqlFetch(CALCULATE_NEXT_URL_USER_QUERY, {});

    const fetchedUserData = userQueryResult.user;

    if (!fetchedUserData) {
        Log.error(
            "Failed to fetch user data for calculateNextUrl",
            Errors.Internal,
        );
        // The best we can do.
        return validatedContinueUrl;
    }
    const userData = fetchedUserData;

    // TODO(WEB-2440): Verify if isFirstLogin can be merged with the code in
    // analytics-cookies.js
    if (isFirstLogin) {
        // If you're a teacher or a district/school admin, we want to take
        // you to your new homepage first, so that you can't miss it. (When
        // a user is both a teacher and a district/school admin, the
        // district case takes priority.)
        if (userData.canAccessDistrictsHomepage) {
            validatedContinueUrl = "/districts";
        } else if (userData.isTeacher && !isAKhanmigoEnrollmentFlow) {
            const teacherWelcomePath = "/teacher/welcome";
            if (isExtraInfoPage) {
                const divider = validatedContinueUrl.includes("?") ? "&" : "?";
                validatedContinueUrl += `${divider}continue=${teacherWelcomePath}`;
            } else {
                validatedContinueUrl = teacherWelcomePath;
            }
        }

        nextUrl = nextUrlAfterFirstTimeLogin(userData, validatedContinueUrl);
    } else {
        nextUrl = validatedContinueUrl;
    }

    // When the user logs in on the main Khan Academy homepage, redirect them
    // to the Khan Academy Classroom experience if they have classrooms in
    // that experience.
    //
    // TODO(marksandstrom) Don't set this if the user is already on the
    // classroom subdomain.
    //
    // TODO(marksandstrom) Having a continueUrl with these special overrides
    // is pretty confusing, especially since the continueUrl specified in
    // code may need to apply to both the classic and classroom experience
    // depending on where the user is being redirected. We should either 1)
    // update the callers of calculateNextUrl to pass in the correct
    // continueUrl or 2) eliminate calculateNextUrl and return the nextUrl
    // from the backend, which can look up where the user should go
    // directly.
    const parsedNextUrl: URL = parseUrl(nextUrl);

    if (userData.isInKhanClassroomDistrict) {
        // We only send specific paths to the Khan Academy Classroom
        // experience. This is so we don't break any flows that take the user
        // through the classic experience, like the district
        // "account-choice" flow or sending the user to the /districts on
        //  first login if they can access the districts page.
        const path = parsedNextUrl.pathname;

        if (path === "/") {
            // Note: for all the / routes, maintain the query parameters.
            if (
                userData.homepageUrl === "/districts" ||
                userData.homepageUrl === "/parent"
            ) {
                // If the user is going to end up on the /districts or /parent
                // page, just send them directly there. No point in sending
                // them to the Khan Academy Classroom experience just to be
                // redirected back.
                nextUrl = userData.homepageUrl + parsedNextUrl.search;
            } else {
                nextUrl = getClassroomUrl() + "/" + parsedNextUrl.search;
            }
        } else if (path === "/teacher/welcome") {
            // The classroom experience includes teacher onboarding, so we
            // don't redirect to this classic onboarding page.
            nextUrl = getClassroomUrl() + "/";
        } else if (path.match(/^\/profile\/me\/class\/[^/]+$/)) {
            // This path is a learner class link in classic. Take the user to
            // the class in Polaris if they have Polaris enabled.
            //
            // Note that just because this user is a user in a district with
            // the Khan Academy Classroom offering doesn't mean that this
            // specific classroom is a Khan Academy Classroom classroom. If
            // it isn't, the user will be redirected to the classic
            // experience.
            const classCode = path.slice("/profile/me/class/".length);

            nextUrl = getClassroomUrl() + "/learner/class/" + classCode;
        }
    }

    // Verify if the user needs to be transferred to a different domain and
    // return the value of the new URL.
    nextUrl = await maybeAddAuthTransfer(gqlFetch, location, userData, nextUrl);
    return nextUrl;
};

// Strings can be double-encoded which decodeURIComponent doesn't account for,
// so we need to loop it ourselves
const decodeString = (str: string): string => {
    let previousString = str;
    let decodedString = decodeURIComponent(str);

    while (decodedString !== previousString) {
        previousString = decodedString;
        decodedString = decodeURIComponent(decodedString);
    }

    return decodedString;
};
